import { computed, ref, type Ref } from 'vue'
import {
  DEFAULT_LOCALE,
  M6_PICKER_LOCALES,
  PLATFORM_LOCALES,
  normalizeLocale,
  type LocaleCode,
  type LocaleOption,
} from './catalog'
import { messages } from './messages'

const STORAGE_KEY = 'mybonet.locale'

export type I18nApi = {
  locale: Ref<LocaleCode>
  available: Ref<LocaleOption[]>
  setLocale: (code: string) => void
  t: (key: string) => string
}

export function createI18n(options?: {
  initial?: string
  enabled?: LocaleCode[]
  fallback?: LocaleCode
}): I18nApi {
  const fallback = options?.fallback ?? DEFAULT_LOCALE
  const enabledCodes = options?.enabled ?? M6_PICKER_LOCALES
  const available = ref(
    PLATFORM_LOCALES.filter((x) => enabledCodes.includes(x.code)),
  )

  const fromUrl = new URLSearchParams(window.location.search).get('locale')
  const fromStore = localStorage.getItem(STORAGE_KEY)
  const locale = ref(
    normalizeLocale(options?.initial ?? fromUrl ?? fromStore ?? fallback),
  )

  function setLocale(code: string) {
    const next = normalizeLocale(code)
    locale.value = enabledCodes.includes(next) ? next : fallback
    localStorage.setItem(STORAGE_KEY, locale.value)
    const url = new URL(window.location.href)
    url.searchParams.set('locale', locale.value)
    window.history.replaceState({}, '', url)
  }

  function t(key: string): string {
    const entry = messages[key]
    if (!entry) return key
    return (
      entry[locale.value] ??
      entry[fallback] ??
      entry[DEFAULT_LOCALE] ??
      key
    )
  }

  // Ensure initial locale is in enabled set (or fallback).
  if (!enabledCodes.includes(locale.value)) {
    locale.value = fallback
  }

  return { locale, available, setLocale, t }
}

export function useT(i18n: I18nApi) {
  return computed(() => (key: string) => i18n.t(key))
}
